import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import {
  getExperiments,
  createExperiment,
  deleteExperiment,
  getPrompt
} from '../services/api';
import PromptTabs from '../components/PromptTabs';

const PromptExperiments = ({ user }) => {
  const { promptId } = useParams();
  const navigate = useNavigate();
  const [experiments, setExperiments] = useState([]);
  const [prompt, setPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newExperiment, setNewExperiment] = useState({
    name: '',
    description: '',
    stickyType: 'user_id'
  });

  useEffect(() => {
    loadData();
  }, [promptId]);

  const loadData = async () => {
    try {
      const [promptResponse, experimentsResponse] = await Promise.all([
        getPrompt(promptId),
        getExperiments(promptId, user.id)
      ]);
      
      setPrompt(promptResponse);
      setExperiments(experimentsResponse);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExperiment = async (e) => {
    e.preventDefault();
    try {
      const experimentData = {
        ...newExperiment,
        promptId,
        userId: user.id
      };
      
      await createExperiment(experimentData);
      setShowCreateModal(false);
      setNewExperiment({ name: '', description: '', stickyType: 'user_id' });
      loadData();
    } catch (error) {
      console.error('Error creating experiment:', error);
      alert('Failed to create experiment');
    }
  };

  const handleDeleteExperiment = async (experimentId) => {
    if (window.confirm('Are you sure you want to delete this experiment?')) {
      try {
        await deleteExperiment(experimentId, user.id);
        loadData();
      } catch (error) {
        console.error('Error deleting experiment:', error);
        alert('Failed to delete experiment');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running': return 'text-celadon-700 bg-celadon-100';
      case 'paused': return 'text-gray-600 bg-gray-100';
      case 'completed': return 'text-gray-700 bg-gray-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      case 'archived': return 'text-gray-500 bg-gray-50';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon-500"></div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Prompt not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">Experiments</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Experiments</h1>
          <p className="text-gray-600 mt-2">
            Manage A/B tests and traffic splitting for {prompt.name}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Experiment
        </button>
      </div>

      {/* Navigation Tabs */}
      <PromptTabs promptId={promptId} />

      {/* Experiments List */}
      {experiments.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-400 text-lg mb-4">🧪</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No experiments yet</h3>
          <p className="text-gray-600 mb-4">
            Create your first experiment to start A/B testing prompt versions
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-celadon-600 text-white rounded-lg hover:bg-celadon-700 transition-colors"
          >
            Create First Experiment
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {experiments.map((experiment) => (
            <div key={experiment.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {experiment.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(experiment.status)}`}>
                      {experiment.status}
                    </span>
                  </div>
                  
                  {experiment.description && (
                    <p className="text-gray-600 mb-3">{experiment.description}</p>
                  )}
                  
                  <div className="flex items-center gap-6 text-sm text-gray-500">
                    <span>
                      Sticky: {experiment.sticky_type}
                    </span>
                    <span>
                      Versions: {experiment.version_count}
                    </span>
                    <span>
                      Traffic: {experiment.total_traffic}%
                    </span>
                    <span>
                      Created: {new Date(experiment.created_at).toLocaleDateString()}
                    </span>
                    {experiment.created_by_name && (
                      <span>
                        By: {experiment.created_by_name}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/experiments/${experiment.id}`)}
                    className="px-3 py-1 text-celadon-600 hover:bg-celadon-50 rounded transition-colors"
                  >
                    View
                  </button>
                  <button
                    onClick={() => handleDeleteExperiment(experiment.id)}
                    className="px-3 py-1 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Create New Experiment</h2>
            
            <form onSubmit={handleCreateExperiment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={newExperiment.name}
                  onChange={(e) => setNewExperiment({ ...newExperiment, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-celadon-500 focus:border-transparent"
                  required
                  placeholder="e.g., A/B Test Version Comparison"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newExperiment.description}
                  onChange={(e) => setNewExperiment({ ...newExperiment, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-celadon-500 focus:border-transparent"
                  rows="3"
                  placeholder="Describe what you're testing..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sticky Routing
                </label>
                <select
                  value={newExperiment.stickyType}
                  onChange={(e) => setNewExperiment({ ...newExperiment, stickyType: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-celadon-500 focus:border-transparent"
                >
                  <option value="user_id">User ID</option>
                  <option value="session_id">Session ID</option>
                  <option value="input_hash">Input Hash</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  How to ensure consistent experience for users
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-celadon-600 text-white rounded-lg hover:bg-celadon-700 transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptExperiments; 